/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-use-before-define */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { toast } from 'react-toastify';
import { formatRFC3339, format } from 'date-fns';
import ApiService from '@services/api';
import { useSession } from '@hooks/useSession';
import {
  MdDownload,
  MdRemoveRedEye,
  MdContentCopy,
  MdPrint,
  MdEmail,
} from 'react-icons/md';

import { downloadFile } from '@utils/download';
import Loading from '@components/Loading';
import RoundButton from '@components/RoundButton';
import Modal from '@components/Modal';
import NotAuthenticated from '@components/NotAuthenticated';
import SelectInput from '@components/SelectInput';
import Input from '@components/Input';
import Button from '@components/Button';
import Paginator from '@components/Paginator';
import settingsStore from '@store/SettingsStore';
import { StatusColor } from '@styles/colors';
import SendEmailDialog from '@components/SendEmailDialog';
import IMDFe from '../../interfaces/IMDFe';

import {
  Container,
  Title,
  Selection,
  Row,
  Field,
  Buttons,
  DateInput,
  Table,
  ContainerEmissao,
  WrapperStatus,
  ContainerStatus,
  Status,
  Actions,
} from './styles';

const currencyFormat = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const MDFeDocs: React.FC = observer(() => {
  const params = useParams();
  const { isMigrating } = useSession();
  const { api } = new ApiService(params.token);

  const modeloOptions = useMemo(() => {
    return [
      { label: 'Todos', value: 1 },
      { label: '58 (MDF-e)', value: 58 },
    ].filter((option) => {
      if (option.value === 1) return true;
      // Por enquanto mostra sempre ou verifica no settingsStore se habilitado
      return true;
    });
  }, [settingsStore.models]);

  const [dataIniSel, setDataIniSel] = useState(
    formatRFC3339(new Date()).substr(0, 10)
  );
  const [dataFimSel, setDataFimSel] = useState(
    formatRFC3339(new Date()).substr(0, 10)
  );
  const [modeloSel, setModeloSel] = useState(modeloOptions[0]);
  const [serieSel, setSerieSel] = useState('');
  const [nroIniSel, setNroIniSel] = useState('');
  const [nroFimSel, setNroFimSel] = useState('');

  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [rejeicao, setRejeicao] = useState('');
  const [mdfes, setMdfes] = useState<IMDFe[]>([]);
  const [page, setPage] = useState(1);
  const [totalMdfes, setTotalMdfes] = useState(0);
  const [queryCount, setQueryCount] = useState(0);
  const [emailModalData, setEmailModalData] = useState<{
    mdfe: IMDFe;
    email: string;
  } | null>(null);

  useEffect(() => {
    if (!isMigrating) {
      loadMdfes();
    }
  }, [page, isMigrating]);

  async function loadMdfes() {
    try {
      setLoading(true);

      const queryParams: any = {
        page,
        dateStart: dataIniSel,
        dateEnd: dataFimSel,
      };
      if (modeloSel.value !== 1) {
        queryParams.model = modeloSel.value;
      }
      if (serieSel) {
        queryParams.serie = serieSel;
      }
      if (nroIniSel) {
        queryParams.numberStart = nroIniSel;
      }
      if (nroFimSel) {
        queryParams.numberEnd = nroFimSel;
      }

      const resp = await api.get('/mdfe/docs', {
        params: queryParams,
      });
      setMdfes(resp.data.items);
      setTotalMdfes(resp.data.total);
      setQueryCount(resp.data.queryCount);
      setAuthenticated(true);
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        setAuthenticated(false);
      } else {
        console.log(error.stack);
        toast.error('Falha ao carregar');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleDownload(mdfe: IMDFe) {
    try {
      const resp = await api.get(`/mdfe/docs/xml/${mdfe.id}`, {
        responseType: 'blob',
      });
      if (mdfe.statusId === 101 || mdfe.statusId === 132) {
        const dh = format(new Date(), 'yyyyMMdd-HHmmss');
        downloadFile(resp.data, `xml-mdfe-${dh}.zip`);
      } else {
        downloadFile(
          resp.data,
          `${mdfe.protocol}_v${mdfe.version}-procMDFe.xml`
        );
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        toast.error('Download indisponível');
      } else {
        toast.error('Falha ao realizar o download');
      }
    }
  }

  async function handlePDF(mdfe: IMDFe) {
    try {
      const resp = await api.get(`/pdf/mdfe/${mdfe.key}`, {
        responseType: 'blob',
      });
      const urlObj = window.URL.createObjectURL(resp.data);
      window.open(urlObj, '_blank')?.focus();
    } catch (error: any) {
      if (error?.response?.status === 404) {
        toast.error('PDF indisponível');
      } else {
        toast.error('Falha ao gerar o PDF');
      }
    }
  }

  async function handleEmail(mdfe: IMDFe, recipientEmail: string) {
    try {
      await api.post(`/email/mdfe/${mdfe.key}`, {
        email: recipientEmail,
      });
      toast.success('E-mail agendado com sucesso');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Falha ao enviar E-mail');
    }
  }

  async function handleCopyClipboard(key: string) {
    try {
      const textField = document.createElement('textarea');
      textField.innerText = key;
      document.body.appendChild(textField);
      textField.select();
      document.execCommand('copy');
      textField.remove();
      toast.success('Chave copiada para a área de transferência');
    } catch (error) {
      toast.error('Falha ao copiar a chave de acesso');
    }
  }

  function renderEmissao(nota: IMDFe) {
    return (
      <ContainerEmissao>
        {format(new Date(nota.issueDate), 'dd/MM/yyyy HH:mm')}
      </ContainerEmissao>
    );
  }

  function renderStatus(cStat: number, xStat: string) {
    if (cStat === 100)
      return (
        <Status style={{ background: StatusColor.completed }}>
          Autorizado
        </Status>
      );
    if (cStat === 101)
      return (
        <Status style={{ background: StatusColor.cancelled }}>Cancelado</Status>
      );
    if (cStat === 132)
      return <Status style={{ background: '#303f9f' }}>Encerrado</Status>;
    if (cStat > 200)
      return (
        <>
          <Status style={{ background: StatusColor.failed }}>Rejeitado</Status>{' '}
          <RoundButton
            hint="Visualizar Rejeição"
            onClick={() => setRejeicao(xStat)}
          >
            <MdRemoveRedEye size={18} />
          </RoundButton>
        </>
      );
    return xStat;
  }

  if (loading)
    return (
      <Container>
        <Loading />
      </Container>
    );

  if (!authenticated) {
    return <NotAuthenticated />;
  }

  return (
    <Container>
      <Title>MANIFESTOS DE DOCUMENTOS FISCAIS</Title>
      <Selection>
        <Row>
          <div style={{ marginRight: 10 }}>
            <Field>
              Emissão
              <div>
                <DateInput
                  value={dataIniSel}
                  onChange={(e: any) => setDataIniSel(e.target.value)}
                />{' '}
                a{' '}
                <DateInput
                  value={dataFimSel}
                  onChange={(e: any) => setDataFimSel(e.target.value)}
                />
              </div>
            </Field>
          </div>
          <Field>
            Modelo
            <SelectInput
              fullWidth
              options={modeloOptions}
              defaultValue={modeloOptions[0]}
              value={modeloSel}
              onChange={(newValue: any) => setModeloSel(newValue)}
            />
          </Field>
          <Field>
            Série
            <Input
              onlyNumber
              value={serieSel}
              onChange={(e: any) => {
                setSerieSel(e.target.value);
              }}
            />
          </Field>
          <Field>
            Número Inicial
            <Input
              onlyNumber
              value={nroIniSel}
              onChange={(e: any) => setNroIniSel(e.target.value)}
            />
          </Field>
          <Field>
            Número Final
            <Input
              onlyNumber
              value={nroFimSel}
              onChange={(e: any) => setNroFimSel(e.target.value)}
            />
          </Field>
        </Row>

        <Buttons>
          <Button
            onClick={() => {
              setPage(1);
              loadMdfes();
            }}
          >
            Consultar
          </Button>
        </Buttons>
      </Selection>

      {mdfes.length > 0 && (
        <>
          <Table>
            <thead>
              <tr>
                <th style={{ width: '10%' }}>Número</th>
                <th style={{ width: '8%' }}>Série</th>
                <th style={{ width: '5%' }}>Modelo</th>
                <th style={{ width: '20%' }}>Emissão</th>
                <th style={{ width: '10%' }}>Valor Carga</th>
                <th style={{ width: '20%' }}>Status</th>
                <th style={{ width: '7%' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {mdfes.map((mdfe) => (
                <tr key={mdfe.id}>
                  <td>{mdfe.number}</td>
                  <td>{mdfe.serie}</td>
                  <td>{mdfe.model}</td>
                  <td>{renderEmissao(mdfe)}</td>
                  <td>{mdfe.total ? currencyFormat.format(mdfe.total) : ''}</td>
                  <td>
                    <WrapperStatus>
                      <ContainerStatus>
                        {mdfe.receiptDate &&
                          format(
                            new Date(mdfe.receiptDate),
                            'dd/MM/yyyy HH:mm'
                          )}
                        {renderStatus(mdfe.statusId, mdfe.status)}
                      </ContainerStatus>
                    </WrapperStatus>
                  </td>
                  <td>
                    <Actions>
                      <RoundButton
                        hint="Copiar Chave de Acesso"
                        onClick={() => handleCopyClipboard(mdfe.key)}
                      >
                        <MdContentCopy size={18} />
                      </RoundButton>
                      <RoundButton
                        hint="Download"
                        onClick={() => handleDownload(mdfe)}
                      >
                        <MdDownload size={18} />
                      </RoundButton>
                      <RoundButton
                        hint="Gerar PDF"
                        onClick={() => handlePDF(mdfe)}
                      >
                        <MdPrint size={18} />
                      </RoundButton>
                      <RoundButton
                        hint="Enviar E-mail"
                        onClick={() =>
                          setEmailModalData({
                            mdfe,
                            email: '',
                          })
                        }
                      >
                        <MdEmail size={18} />
                      </RoundButton>
                    </Actions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div>
            Exibindo {page > 1 ? (page - 1) * 20 + 1 : 1} a{' '}
            {page > 1 ? (page - 1) * 20 + queryCount : queryCount} de um total
            de {totalMdfes} manifestos.
          </div>
        </>
      )}

      {totalMdfes > 20 && (
        <Paginator
          activePage={Number(page)}
          limitPage={20}
          totalRecords={totalMdfes}
          onChange={(newPage) => setPage(newPage)}
        />
      )}

      {rejeicao && (
        <Modal onCloseRequest={() => setRejeicao('')} exitButton={false}>
          <div style={{ padding: 40 }}>{rejeicao}</div>
        </Modal>
      )}

      {emailModalData && (
        <SendEmailDialog
          isOpen={!!emailModalData}
          initialEmail={emailModalData.email}
          onConfirm={(email) => {
            handleEmail(emailModalData.mdfe, email);
            setEmailModalData(null);
          }}
          onCancel={() => setEmailModalData(null)}
        />
      )}
    </Container>
  );
});

export default MDFeDocs;
